import { get, post } from './client'

export type ValidatorSeverity = 'critical' | 'warning' | 'info'
export type ValidatorCategory = 'orders' | 'products' | 'inventory' | 'shipping' | 'finance' | 'suppliers' | 'purchase-orders'

export type AffectedRow = {
  rowId:   string
  display: string
  fields:  Record<string, string>
  fixable: string[]
}

export type ValidatorReport = {
  validatorId:   string
  title:         string
  severity:      ValidatorSeverity
  affectedCount: number
  totalRows:     number
}

export type CategoryReport = {
  category:      ValidatorCategory
  healthPct:     number
  issueCount:    number
  criticalCount: number
  warningCount:  number
  totalRows:     number
  lastRun:       string
  status:        'healthy' | 'needs_attention' | 'critical' | 'not_configured'
  validators:    ValidatorReport[]
}

export type WorkflowBlocker = {
  workflowId:    string
  validatorId:   string
  affectedCount: number
}

export type WorkflowReadiness = {
  workflowId: string
  label:      string
  ready:      boolean
  blockers:   WorkflowBlocker[]
}

export type HealthReport = {
  shop:              string
  overallScore:      number
  readinessLevel:    'ready' | 'needs_attention' | 'critical'
  categories:        CategoryReport[]
  workflowReadiness: WorkflowReadiness[]
  generatedAt:       string
  totalIssues:       number
  totalCritical:     number
}

export type IssueDetail = {
  validator: { id: string; title: string; severity: ValidatorSeverity; description: string }
  result:    { validatorId: string; affectedCount: number; totalRows: number; affectedRows: AffectedRow[] }
}

// ── Shopify Catalog Audit ─────────────────────────────────────────────────────

export type CatalogIssueSeverity = 'critical' | 'warning'

export type CatalogIssue = {
  variantId:    string
  productTitle: string
  displayName:  string
  sku:          string | null
  severity:     CatalogIssueSeverity
  code:         string
  title:        string
  description:  string
}

export type CatalogAuditResult = {
  totalVariants:  number
  activeVariants: number
  issues:         CatalogIssue[]
  criticalCount:  number
  warningCount:   number
  generatedAt:    string
}

// ── Order Stats ───────────────────────────────────────────────────────────────

export type OrderStats = {
  total:      number
  fulfilled:  number
  inProgress: number
  returns:    number
  cancelled:  number
}

// ── Settings Audit ────────────────────────────────────────────────────────────

export type SettingsIssueSeverity = 'critical' | 'warning' | 'info'

export type SettingsIssue = {
  workflow:    'confirmation' | 'sales' | 'shipping_confirmation' | 'customer_service' | 'cross'
  severity:    SettingsIssueSeverity
  code:        string
  title:       string
  description: string
  field?:      string
}

export type SettingsAuditResult = {
  issues:        SettingsIssue[]
  criticalCount: number
  warningCount:  number
  infoCount:     number
  generatedAt:   string
}

// ── API client ────────────────────────────────────────────────────────────────

export const healthApi = {
  getReport:   (shop: string): Promise<HealthReport> =>
    get(`/system-health/report?shop=${encodeURIComponent(shop)}`),

  runScan:     (shop: string, categories?: ValidatorCategory[]): Promise<HealthReport> =>
    post(`/system-health/run?shop=${encodeURIComponent(shop)}`, categories ? { categories } : {}),

  getIssues:   (shop: string, validatorId: string): Promise<IssueDetail> =>
    get(`/system-health/issues/${encodeURIComponent(validatorId)}?shop=${encodeURIComponent(shop)}`),

  getBlockers: (shop: string): Promise<{ workflowReadiness: WorkflowReadiness[] }> =>
    get(`/system-health/blockers?shop=${encodeURIComponent(shop)}`),

  getCatalogAudit: (shop: string): Promise<CatalogAuditResult> =>
    get(`/system-health/shopify-catalog?shop=${encodeURIComponent(shop)}`),

  getSettingsAudit: (shop: string): Promise<SettingsAuditResult> =>
    get(`/system-health/settings-audit?shop=${encodeURIComponent(shop)}`),

  getOrderStats: (shop: string): Promise<OrderStats> =>
    get(`/system-health/order-stats?shop=${encodeURIComponent(shop)}`),
}
